import {
    DataProxy,
    useLazyQuery,
    useMutation,
    useQuery,
    useReactiveVar,
} from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'

import {
    FirmResponse,
    UserWithProjectRole,
} from '@deltek/specification-client/core/Models/firm'
import {
    GetFilterInput,
    GetResultsInput,
    GetSortInput,
} from '@deltek/specification-client/core/Models/projectItem'
import {
    SpecSortingRule,
    TableFetchNextDataProps,
} from '@deltek/specification-client/core/Widgets/SpecTable'
import {
    AssignmentInput,
    ProjectTeamInput,
    UpdateAssignmentDataInput,
} from '@deltek/specification-client/core/models/ProjectTeamInput'
import Dialog from '@material-ui/core/Dialog/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import { Theme, makeStyles } from '@material-ui/core/styles'
import { AllProjectTeamResponse, Assignment, ProjectTeam } from 'Models/project'
import {
    UpdateProjectTeamAssignmentMutation,
    UpdateProjectTeamMutation,
} from 'api/graphQL/mutations/project.mutations'
import {
    FirmProfessionalDisciplineQuery,
    FirmUsersProjectMembershipQuery,
} from 'api/graphQL/queries/firm.queries'
import { GetProjectTeamQuery } from 'api/graphQL/queries/project.queries'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import { FirmProfessionalDisciplineResponse } from 'api/graphQL/types/firm.types'
import { MutationProjectTeamAssignmentResponse } from 'api/graphQL/types/project.types'
import React, { useCallback, useEffect, useState } from 'react'
import { IdType } from 'react-table'
import ManageProjectTeamPopupFormFooter from './ManageProjectTeamPopupFormFooter'
import ManageProjectTeamPopupFormHeader from './ManageProjectTeamPopupFormHeader'
import {
    ProjectTeamFilterContext,
    ProjectTeamFilterContextInterface,
} from './ProjectTeamFilterContext'
import ProjectTeamGrid from './ProjectTeamGrid'

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        backgroundColor: theme.palette.primary.main,
        height: 170,
    },
    dialogTitle: {
        padding: 0,
    },
    dialogContent: {
        border: 0,
    },
    dialogFooter: {
        border: '1px solid #C9C9C9',
    },
}))

export interface ManageProjectTeamPopupProps {
    projectId: string
    open: boolean
    handleDialogClose?: VoidFunction
    variant: 'add' | 'remove'
    projectTeam?: string | null
}

const defaultPageSize: number = 50
const defaultSorting: Partial<GetSortInput>[] = [
    { sortField: 'discipline', sortOrder: 'asc' },
]
const initialSorting = [{ id: 'discipline', desc: false }]

export const ManageProjectTeamPopup: React.FC<ManageProjectTeamPopupProps> = ({
    projectId,
    open,
    handleDialogClose,
    variant,
    projectTeam,
}) => {
    const classes = useStyles()

    const [sortBy, setSortBy] = React.useState<
        SpecSortingRule<UserWithProjectRole>[]
    >(initialSorting)
    const [skipCount, setSkipCount] = React.useState<number>(0)
    const [userFilterValues, setUserFilterValues] = React.useState<
        Partial<GetFilterInput>[]
    >([])

    const [formDirty, setFormDirty] = React.useState<boolean>(false)

    const [selectedRowIds, setSelectedRowIds] = useState<
        Record<IdType<UserWithProjectRole>, boolean>
    >({})

    const { currentUserInfo } = useReactiveVar(userInfo)
    const firmId = currentUserInfo?.account.accountId

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const {
        data: professionalDisciplinesData,
        loading: professionalDisciplinesLoading,
        error: professionalDisciplinesError,
    } = useQuery<FirmProfessionalDisciplineResponse>(
        FirmProfessionalDisciplineQuery,
        {
            variables: {
                firmId: firmId ?? '',
            },
        }
    )

    const [
        getUserFilters,
        {
            data: usersData,
            loading: usersLoading,
            error: usersError,
            fetchMore,
            networkStatus: userDataStatus,
        },
    ] = useLazyQuery<FirmResponse>(FirmUsersProjectMembershipQuery, {
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
    })

    const [
        updateProjectTeam,
        {
            loading: updateProjectTeamLoading,
            error: updateProjectTeamError,
            data: updateProjectTeamData,
        },
    ] = useMutation<
        { updateProjectTeam: Partial<ProjectTeam> | null },
        { updateProjectTeamInput: ProjectTeamInput }
    >(UpdateProjectTeamMutation)
    const [
        updateAssignment,
        {
            loading: updateAssignmentLoading,
            error: updateAssignmentError,
            data: updateAssignmentData,
        },
    ] = useMutation<
        { updateProjectTeamAssignment: Partial<Assignment> | null },
        { updateAssignmentDataInput: UpdateAssignmentDataInput }
    >(UpdateProjectTeamAssignmentMutation, {
        update(
            cache: DataProxy,
            result: MutationProjectTeamAssignmentResponse
        ) {
            if (!!result.data) {
                const variables = {
                    firmId: firmId ?? '',
                    projectId: projectId ?? '',
                    resultInput: {
                        limit: defaultPageSize,
                        skip: 0,
                        sortOrders: getSortOrder(),
                        filters: getFilterValues(),
                    } as Partial<GetResultsInput>,
                }
                const firmResponse = cache.readQuery<FirmResponse>({
                    query: FirmUsersProjectMembershipQuery,
                    variables,
                })

                cache.writeQuery({
                    query: FirmUsersProjectMembershipQuery,
                    variables,
                    data: {
                        firm: {
                            ...firmResponse?.firm,
                            usersWithProjectMembership: {
                                count:
                                    firmResponse?.firm
                                        ?.usersWithProjectMembership?.count,
                                users: firmResponse?.firm?.usersWithProjectMembership?.users?.map(
                                    (userWithProjectRole) =>
                                        userWithProjectRole.user.id ===
                                        result.data?.updateProjectTeamAssignment
                                            ?.user?.id
                                            ? {
                                                  ...userWithProjectRole,
                                                  isAssignedToProject: true,
                                                  projectRole:
                                                      result.data
                                                          ?.updateProjectTeamAssignment
                                                          ?.projectRole,
                                              }
                                            : userWithProjectRole
                                ),
                            },
                        },
                    } as FirmResponse,
                })
            }
        },
    })

    const [
        getProjectTeam,
        {
            data: projectTeamData,
            loading: projectTeamLoading,
            error: projectTeamError,
        },
    ] = useLazyQuery<AllProjectTeamResponse>(GetProjectTeamQuery)

    const updateProjectAssignment = (
        projectRole: string,
        description: string,
        userid: string
    ) => {
        const updateAssignmentDataInput: UpdateAssignmentDataInput = {
            description: description,
            projectRole: projectRole,
            firmId: firmId!,
            userId: userid,
            projectId: projectId,
        }

        return updateAssignment({
            variables: { updateAssignmentDataInput },
        })
    }
    const getSortOrder = React.useCallback(() => {
        const newSortBy = sortBy.filter(
            (value) => value.id !== defaultSorting[0].sortField
        )

        if (newSortBy.length > 0) {
            return [
                ...defaultSorting,
                ...newSortBy.map((each) => ({
                    sortField: each.id,
                    sortOrder: each.desc ? 'desc' : 'asc',
                })),
            ]
        }
        return defaultSorting
    }, [sortBy])

    const getFilterValues = React.useCallback(() => {
        const newFilter: GetFilterInput[] = [
            {
                itemField: 'isAssignedToProject',
                filterValues: [variant === 'add' ? 'false' : 'true'],
            },
        ]
        if (!!projectTeam) {
            newFilter.push({
                itemField: 'discipline',
                filterValues: [projectTeam],
            })
        }

        return [...newFilter, ...userFilterValues]
    }, [variant, userFilterValues, projectTeam])

    const triggerProjectReload = useCallback(() => {
        getProjectTeam({
            variables: {
                projectId: projectId ?? '',
            },
        })
    }, [getProjectTeam, projectId])

    const getUserAssignment = useCallback(
        (userId: string) => {
            return projectTeamData?.getProject.projectTeam?.assignments?.find(
                (assignment) => assignment.user?.id === userId
            )
        },
        [projectTeamData]
    )

    const getRoles = useCallback(
        (disciplineName: string): Record<'name' | 'value', string>[] => {
            const disciplineData = (
                professionalDisciplinesData?.firm?.professionalDisciplines || []
            ).find((discipline) => discipline.name === disciplineName)
            if (!!disciplineData) {
                const data = disciplineData.roles || []
                return data.map((value) => ({ name: value, value: value }))
            }
            return []
        },
        [professionalDisciplinesData]
    )

    const triggerProjectFilter = React.useCallback(() => {
        setSkipCount(0)
        triggerProjectReload()
        getUserFilters({
            variables: {
                firmId: firmId ?? '',
                projectId: projectId ?? '',
                resultInput: {
                    limit: defaultPageSize,
                    skip: 0,
                    sortOrders: getSortOrder(),
                    filters: getFilterValues(),
                } as Partial<GetResultsInput>,
            },
        })
    }, [
        getUserFilters,
        firmId,
        projectId,
        getSortOrder,
        triggerProjectReload,
        getFilterValues,
    ])

    React.useEffect(() => {
        triggerProjectFilter()
    }, [getUserFilters, userFilterValues, sortBy, triggerProjectFilter])

    const fetchNextData = React.useCallback(
        ({
            totalRows,
        }: Partial<TableFetchNextDataProps<UserWithProjectRole>>) => {
            let updatedSkipCount = (skipCount ?? 0) + defaultPageSize
            setSkipCount(updatedSkipCount)
            if (fetchMore) {
                fetchMore({
                    variables: {
                        firmId: firmId ?? '',
                        projectId: projectId ?? '',
                        resultInput: {
                            skip: updatedSkipCount,
                            limit: defaultPageSize,
                            sortOrders: getSortOrder(),
                            filters: getFilterValues(),
                        } as Partial<GetResultsInput>,
                    },
                })
            }
        },
        [
            skipCount,
            setSkipCount,
            fetchMore,
            firmId,
            projectId,
            getSortOrder,
            getFilterValues,
        ]
    )

    const [dialogOpen, setDialogOpen] = useState<boolean>(false)
    useEffect(() => {
        setDialogOpen(open)
    }, [open])

    const onDialogClose = useCallback(() => {
        setDialogOpen(false)
        handleDialogClose?.()
    }, [handleDialogClose])

    const onFormSubmit = () => {
        const { projectTeam } = projectTeamData?.getProject!

        const updateProjectTeamInput: ProjectTeamInput = {
            id: projectTeam && projectTeam.id,
            projectId: projectId,
            firmId: firmId!,

            assignments: [],
        }

        const assignments = projectTeamData?.getProject.projectTeam?.assignments

        for (let index = 0; index < (assignments || []).length; index++) {
            const assignment = assignments![index]

            // In case of remove
            if (variant !== 'add') {
                const userIndex = usersData?.firm?.usersWithProjectMembership?.users?.findIndex(
                    (userWithProjectRole) =>
                        userWithProjectRole.user.id === assignment?.user?.id
                )
                if (!selectedRowIds[userIndex!]) {
                    updateProjectTeamInput.assignments.push({
                        userId: assignment?.user?.id,
                        firmId: firmId!,
                        projectRole: assignment?.projectRole!,
                        description: assignment?.description!,
                    })
                }
            } else {
                updateProjectTeamInput.assignments.push({
                    userId: assignment?.user?.id,
                    firmId: firmId!,
                    projectRole: assignment?.projectRole!,
                    description: assignment?.description!,
                })
            }
        }
        if (variant === 'add') {
            for (const [key] of Object.entries(selectedRowIds)) {
                const userProjectRole = usersData?.firm
                    ?.usersWithProjectMembership?.users![parseInt(key)]
                if (
                    !updateProjectTeamInput.assignments.some(
                        (each) => each.userId === userProjectRole?.user.id
                    )
                ) {
                    updateProjectTeamInput.assignments.push({
                        userId: userProjectRole?.user.id,
                        firmId: firmId!,
                    } as AssignmentInput)
                }
            }
        }

        updateProjectTeam({
            variables: { updateProjectTeamInput },
        }).catch(() => {
            // This is a temporary workaround that will be removed once a global
            // GraphQL error handler is implemented.
        })
    }

    const context: ProjectTeamFilterContextInterface = {
        userFilterValues,
        setUserFilterValues,
    }

    useEffect(() => {
        if (usersError) {
            showGlobalSnackbar({
                variables: {
                    id: 'manageProjectTeamPopup_loadUsers',
                    variant: 'error',
                    message: 'Error loading team.',
                },
            })
        }
        if (updateProjectTeamError) {
            showGlobalSnackbar({
                variables: {
                    id: 'manageProjectTeamPopup_updateProjectTeamError',
                    variant: 'error',
                    message: 'Error while saving project team.',
                },
            })
        }
        if (projectTeamError) {
            showGlobalSnackbar({
                variables: {
                    id: 'manageProjectTeamPopup_loadProjectTeamError',
                    variant: 'error',
                    message: 'Error while loding project team.',
                },
            })
        }
        if (updateAssignmentError) {
            showGlobalSnackbar({
                variables: {
                    id: 'manageProjectTeamPopup_updateAssignmentError',
                    variant: 'error',
                    message: 'Error while updating project team.',
                },
            })
        }

        if (professionalDisciplinesError) {
            showGlobalSnackbar({
                variables: {
                    id: 'manageProjectTeamPopup_professionalDisciplinesError',
                    variant: 'error',
                    message: 'Error while loading project role.',
                },
            })
        }
    }, [
        showGlobalSnackbar,
        usersError,
        updateProjectTeamError,
        projectTeamError,
        updateAssignmentError,
        professionalDisciplinesError,
    ])

    React.useEffect(() => {
        if (!showGlobalSnackbar) return
        if (
            !!updateProjectTeamData &&
            updateProjectTeamData.updateProjectTeam
        ) {
            setSelectedRowIds({})
            showGlobalSnackbar({
                variables: {
                    message:
                        variant === 'add'
                            ? `Project team member(s) added successfully.`
                            : `Project team member(s) removed successfully.`,
                    variant: 'success',
                    id: `ProjectTeamSaveSuccess`,
                },
            })
            triggerProjectFilter()

            updateProjectTeamData.updateProjectTeam = null
        }
    }, [
        formDirty,
        updateProjectTeamData,
        showGlobalSnackbar,
        triggerProjectFilter,
        setSelectedRowIds,
        variant,
    ])

    React.useEffect(() => {
        if (!showGlobalSnackbar) return
        if (
            !!updateAssignmentData &&
            updateAssignmentData.updateProjectTeamAssignment
        ) {
            setSelectedRowIds({})

            showGlobalSnackbar({
                variables: {
                    message: `Project team member has been updated and added successfully.`,
                    variant: 'success',
                    id: `ProjectTeamUpdateSuccess`,
                },
            })
            triggerProjectFilter()
            updateAssignmentData.updateProjectTeamAssignment = null
        }
    }, [
        formDirty,
        updateAssignmentData,
        showGlobalSnackbar,
        triggerProjectFilter,
        setSelectedRowIds,
        usersData,
    ])

    useEffect(() => {
        setFormDirty(Object.entries(selectedRowIds).length > 0)
    }, [selectedRowIds, formDirty])

    return (
        <Dialog
            open={dialogOpen}
            onClose={(_event, reason) => {
                if (reason !== 'backdropClick') {
                    onDialogClose()
                }
            }}
            fullWidth={true}
            maxWidth="md"
            disableEscapeKeyDown={true}
            aria-labelledby="draggable-dialog-title"
            data-testid="manageProjectTeamPopup"
            disableEnforceFocus
        >
            <DialogTitle disableTypography className={classes.dialogTitle}>
                <ManageProjectTeamPopupFormHeader
                    onDialogClose={onDialogClose}
                    variant={variant}
                />
            </DialogTitle>

            <DialogContent dividers className={classes.dialogContent}>
                <ProjectTeamFilterContext.Provider value={context}>
                    <ProjectTeamGrid
                        usersData={
                            usersData?.firm?.usersWithProjectMembership?.users
                        }
                        fetchNextData={fetchNextData}
                        userDataStatus={userDataStatus}
                        defaultPageSize={defaultPageSize}
                        setSortBy={setSortBy}
                        sortBy={sortBy}
                        setSelectedRowIds={setSelectedRowIds}
                        selectedRowIds={selectedRowIds}
                        userCount={
                            usersData?.firm?.usersWithProjectMembership
                                ?.count ?? 0
                        }
                        getUserAssignment={getUserAssignment}
                        updateAssignment={updateProjectAssignment}
                        updateAssignmentLoading={updateAssignmentLoading}
                        getRoles={getRoles}
                        canToggleGroup={!projectTeam}
                    />
                </ProjectTeamFilterContext.Provider>
            </DialogContent>

            <DialogActions className={classes.dialogFooter}>
                <ManageProjectTeamPopupFormFooter
                    handleClose={onDialogClose}
                    handleFormSubmit={onFormSubmit}
                    isProgress={
                        usersLoading ||
                        updateProjectTeamLoading ||
                        projectTeamLoading ||
                        updateAssignmentLoading ||
                        professionalDisciplinesLoading
                    }
                    disableSave={!formDirty}
                    variant={variant}
                />
            </DialogActions>
        </Dialog>
    )
}
export default ManageProjectTeamPopup
